import React, {
  useState,
  useEffect,
  forwardRef,
  useRef,
  useMemo,
  useImperativeHandle,
} from 'react';
import {
  Image,
  Space,
  PreviewGroup,
  Modal,
  Popover,
  Button,
  Tooltip,
} from '@bedrock/components';
import { ChevronDown, Upload, Link } from '@bedrock/icons-react';
import { useMemoizedFn } from 'ahooks';
import classNames from 'classnames/bind';
import styles from './index.less';
import {
  SUpload,
  AttachmentDots,
} from '#components/SForm/SFormElement/SUpload';
import TextAreaPopover from '#components/SForm/SFormElement/TextAreaPropover';
import { apiService, defs } from '#services';
import { $sType } from '#utils';
import MuseUploadDrawer from './MuseUploadDrawer';

const cx = classNames.bind(styles);

type ClosingReportProps = {
  quote_key?: string;
  value?: defs.DemandSupplierQuoteSettleReportDto;
  onChange?: (value: defs.DemandSupplierQuoteSettleReportDto) => void;
  view?: boolean;
  muse?: number;
  demandId?: number;
  muse_url?: string;
  indexId?: number;
  standardCode1?: string;
  standardCode2?: string;
  muse_archive_status?: string; // 素材归档状态：ARCHIVED_PENDING_SYNC（归档中）、ARCHIVED_SUCCESS（归档成功）、ARCHIVED_FAILED（归档失败）
  muse_file_stats?: string; // 素材文件统计，格式：共xx个：JPG(xx)、PNG(xx)
};

const ClosingReport = forwardRef((props: ClosingReportProps, ref) => {
  const {
    quote_key,
    muse,
    demandId,
    muse_url,
    indexId,
    standardCode1,
    standardCode2,
    muse_archive_status,
    muse_file_stats,
  } = props;
  const {
    value = { content: '', attachments: [], quote_key, attach_blob_ids: [] },
    onChange,
    view = false,
  } = props;
  const uploadRef = useRef<any>(null);

  const [localFileList, setLocalFileList] = useState<any[]>(
    value?.attachments || []
  );
  const [museDrawerVisible, setMuseDrawerVisible] = useState(false);

  const isMuse = muse === 1;
  const isInner = $sType === 'inner';
  const isMuseArchiving = muse_archive_status === 'ARCHIVED_PENDING_SYNC';

  const isUploading = useMemo(() => {
    return localFileList.some((n) => n.status === 'uploading');
  }, [localFileList]);

  const validateFields = useMemoizedFn(() => {

    return new Promise((resolve, reject) => {
      if (uploadRef.current) {
        uploadRef.current
          ?.validateFields?.()
          .then(() => {
            if (value.content || value.attachments?.length) {
              resolve(true);
            } else {
              reject('请输入结案报告或上传文件');
            }
          })
          .catch(reject);
      } else {
        resolve(true);
      }
    });
  });

  useImperativeHandle(ref, () => {
    return {
      validateFields,
    };
  });

  const renderMuseEntry = () => {
    // 内包端只读模式：展示跳转按钮
    if (isInner && view) {
      return (
        <div>
        <Tooltip title={isMuseArchiving ? undefined : (muse_url ? undefined : '暂无链接')}>
          <Button
            type="link"
            disabled={isMuseArchiving || !muse_url}
            onClick={() => {
              if (muse_url) {
                window.open(muse_url, '_blank');
              }
            }}
          >
            {isMuseArchiving ? '查看素材资源（归档中）' : '查看素材资源'}
          </Button>
        </Tooltip>
        <div className={cx('muse-file-stats')}>{muse_file_stats}</div>
        </div>
      );
    }

    // 供应商端编辑模式：展示上传链接按钮
    if (!view) {
      return (
        <>
          <Button
            type="link"
            icon={<Upload />}
            onClick={() => setMuseDrawerVisible(true)}
          >
            上传素材资源
          </Button>
          <MuseUploadDrawer
            visible={museDrawerVisible}
            onClose={() => setMuseDrawerVisible(false)}
            demandId={demandId!}
            indexId={indexId!}
            standardCode1={standardCode1!}
            standardCode2={standardCode2!}
          />
        </>
      );
    }

    // 供应商端只读模式：展示查看按钮
    return (
      <>
        <Button
          type="link"
          disabled={isMuseArchiving}
          onClick={() => {
            if (!isMuseArchiving) setMuseDrawerVisible(true);
          }}
        >
          {isMuseArchiving ? '查看 Muse 素材（归档中）' : '查看 Muse 素材'}
        </Button>
        {!isMuseArchiving && (
          <MuseUploadDrawer
            visible={museDrawerVisible}
            onClose={() => setMuseDrawerVisible(false)}
            view
            demandId={demandId!}
            indexId={indexId!}
            standardCode1={standardCode1!}
            standardCode2={standardCode2!}
          />
        )}
      </>
    );
  };

  const renderAttachmentEntry = () => {
    return (
      <Space
        className={cx('upload-container', {
          readonly: view,
        })}
        size={[8, 0]}
      >
        {!view && (
          <SUpload
            className={cx('upload-button')}
            fileList={value.attachments as any}
            onChange={(file) => {
              onChange({
                ...value,
                quote_key,
                attachments: file.dataList,
                attach_blob_ids: file.dataList.map((item: any) => item.blob.id),
              });
              setLocalFileList(file.fileList);
            }}
            ref={uploadRef}
            trigger={
              <Tooltip
                title={'上传附件：单个文件不超过 200M'}
                placement="topRight"
              >
                <Button
                  type="checked-neutral"
                  className={cx('upload-trigger')}
                  icon={<Upload />}
                  loading={isUploading}
                />
              </Tooltip>
            }
            tip={null}
          />
        )}
        {!!value?.attachments?.length && (
          <Popover
            content={
              <div className={cx('upload-popover-content')}>
                <SUpload
                  className={cx('upload-list')}
                  maxFileSize={200 * 1024 * 1024}
                  multiple
                  fileList={value.attachments as any}
                  onChange={(file) => {
                    onChange({
                      ...value,
                      attachments: file.dataList,
                      attach_blob_ids: file.dataList.map(
                        (item: any) => item.blob.id
                      ),
                    });
                    setLocalFileList(file.fileList);
                  }}
                  view={view}
                  tip={null}
                />
              </div>
            }
            trigger="click"
            disabled={!value?.attachments?.length}
          >
            <Button
              type="link"
              className={cx('upload-trigger')}
              suffixIcon={<ChevronDown />}
            >
              {view
                ? `${value?.attachments?.length || 0}个附件`
                : `共${value?.attachments?.length || 0}个`}
            </Button>
          </Popover>
        )}
        {!value?.attachments?.length && !!view && (
          <span className={cx('file-empty-tip')}>无附件</span>
        )}
      </Space>
    );
  };

  return (
    <div className={cx('closing-report')}>
      {isMuse ? (
        renderMuseEntry()
      ) : (
        <>
          <div className={cx('text-area-container')}>
            <TextAreaPopover
              view={view}
              value={value.content}
              onChange={(e) => {
                onChange({ ...value, quote_key, content: e?.trim?.() || '' });
              }}
            />
          </div>
          {renderAttachmentEntry()}
        </>
      )}
    </div>
  );
});

export default ClosingReport;
